import { useEffect, useRef } from "react";
import { getSupabaseClient } from "../api/supabaseClient.js";

/** Narrows a `postMessage` payload to the `{ code }` the auth popup sends back. */
function readAuthCodeFromMessage(data: unknown): string | null {
  if (typeof data !== "object" || data === null || !("code" in data)) {
    return null;
  }
  const { code } = data;
  return typeof code === "string" ? code : null;
}

/**
 * Completes popup auth: listens for the code posted back by the sign-in popup
 * and exchanges it for a session. Every screen that can start popup auth needs
 * this, because the popup deliberately doesn't establish the session itself —
 * it lives in a different storage partition than the framed app.
 *
 * Messages that aren't a same-origin auth payload are ignored rather than
 * surfaced as errors: the host page we're framed in (and Vite's HMR channel in
 * development) both post unrelated messages at this window.
 */
export function useAuthPopupSession({
  isEnabled,
  onError,
}: {
  isEnabled: boolean;
  onError: (message: string) => void;
}): void {
  // Callers pass an inline callback; keep it out of the effect's deps so the
  // listener isn't torn down and re-added on every render.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const code = readAuthCodeFromMessage(event.data);
      if (!code) {
        return;
      }

      void getSupabaseClient()
        .auth.exchangeCodeForSession(code)
        .then(({ data: { session }, error }) => {
          if (error) {
            console.error("Error exchanging code for session:", error);
            onErrorRef.current(
              "Authentication failed - invalid session code. Please try again.",
            );
          } else if (session) {
            void getSupabaseClient().auth.setSession(session);
          }
        });
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isEnabled]);
}
